import React from 'react';
import { Button, Position, Text, Tooltip, TextLink, Shelf } from '@chaser/components';
import BottomNotification from '../index';
import ResumeInfoModal from './ResumeInfoModal/index';
import ScheduleChasingModal from './ScheduleChasingModal';
import OrganisationService from '../../../services/organisation-service';
import useOrganisation from '../../../hooks/useOrganisation';
import useFetch from '../../../hooks/useFetch';
import useShortCode from '../../../hooks/useShortCode';
import useEvent from '../../../hooks/useEvent';
import useUserInfo from '../../../hooks/useUserInfo';
import useModal from '../../../hooks/useModal';
import emitter from '../../../util/emitter';
import useBeAbleToStartChase from '../../../hooks/useBeAbleToStartChase';
import RoleGate from '@/components/FeatureGate/RoleGate';
import { roles } from '@/util/constants';

const formatDate = date => {
  const dt = new Date(date);
  dt.setUTCHours(0, 0, 0, 0);

  return dt.toLocaleString('en-gb', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const PausedChasingNotification = ({ sidebarCollapsed, hasSidebar }) => {
  const showModal = useModal();
  const { canStartChase } = useBeAbleToStartChase();

  const shortCode = useShortCode();
  const { data } = useFetch(OrganisationService.getLastChase, shortCode, {
    suspense: false,
  });
  const { noChaseUntil } = useOrganisation();

  const userInfo = useUserInfo();

  useEvent('openStartChasing', props =>
    showModal(
      <ResumeInfoModal
        hasChasedBefore={hasChasedBefore}
        shouldShowResume={props?.shouldShowResume}
      />,
    ),
  );

  if (data === undefined) return null;

  const hasChasedBefore = !!data?.date;

  const tooltipData = {
    content: 'Please verify your email before unpausing chasing',
    disable: !userInfo.hasVerifiedEmail,
  };

  if (!canStartChase) {
    tooltipData.content =
      'Your account has more users, schedules or templates than your subscription allows. Please refer to the banners at the top of the screen for more information.';
    tooltipData.disable = false;
  }

  return (
    <BottomNotification
      sidebarCollapsed={sidebarCollapsed}
      hasSidebar={hasSidebar}
      left={
        <Text color="White">
          {noChaseUntil && 'Resuming on ' + formatDate(noChaseUntil)}{' '}
          <RoleGate roles={[roles.owner, roles.manager, roles.cashflowManager]}>
            <TextLink
              disabled={!canStartChase}
              as="button"
              type="button"
              monochrome
              onClick={() => {
                showModal(<ScheduleChasingModal pauseUntil={noChaseUntil} />);
              }}
            >
              {noChaseUntil ? 'Change Date' : 'Schedule a resume date'}
            </TextLink>
          </RoleGate>
        </Text>
      }
      right={
        <Shelf>
          <Shelf data-hide-on-small="true">
            <Text color="White">
              <TextLink
                as="button"
                type="button"
                monochrome
                onClick={() => {
                  emitter.emit('openStartChasing', { shouldShowResume: false });
                }}
              >
                What happens if I {hasChasedBefore ? 'resume' : 'start'} sending reminders?
              </TextLink>
            </Text>
          </Shelf>

          <Tooltip
            disable={!tooltipData.disable}
            content={tooltipData.content}
            position={Position.TOP}
          >
            <RoleGate
              roles={[roles.manager, roles.owner, roles.cashflowManager]}
              renderGate={(_, children) => {
                return (
                  <Tooltip
                    position={Position.BOTTOM}
                    content="This action needs additional permissions. Please contact your organisation owner to review your permissions."
                  >
                    {React.cloneElement(children, { disabled: true })}
                  </Tooltip>
                );
              }}
            >
              <Button
                onClick={() => emitter.emit('openStartChasing')}
                data-testid="confirmBtn"
                data-trackingid="confirmBtn"
                disabled={!canStartChase || !userInfo.hasVerifiedEmail}
              >
                {hasChasedBefore ? 'Resume sending reminders' : 'Start sending reminders'}
              </Button>
            </RoleGate>
          </Tooltip>
        </Shelf>
      }
    />
  );
};

export default PausedChasingNotification;
